import {
  Banner,
  Box,
  Grid,
  Image,
  InlineStack,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderDescription,
  PageHeaderTitle,
  PageLoader,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { IdentifierType, allEnabledIdentifiers } from '@/features/workspaces';
import { generatePath } from '@/services/router/generate-path';
import segmentLogo from '@/assets/images/integrations/segment.svg';
import { useWorkspaceApiCredentials } from '@/features/integrations';
import { useSegmentInConnection } from '@/services/db/segment-in-integration/queries';
import { SegmentInConnector } from './components/segment-in-connector';
import { SegmentHowItWorks } from './components/segment-how-it-works';
import { useSegmentInBreadcrumbs } from './hooks';

const PAGE_TITLE = 'Segment destination';
const LOADING_LABEL = 'Loading integration…';

/** Named from Segment's side: this connector feeds the workspace, and Customer.io is its destination. */
const SEGMENT_DESTINATION_DOC_URL =
  'https://segment.com/docs/connections/destinations/catalog/actions-customerio/?utm_source=partners&utm_medium=docs&utm_campaign=customer.io';
const SEGMENT_MIGRATE_DOC_URL = 'https://docs.customer.io/integrations/data-in/connections/cdps/segment/#migrate';

/** The outbound half of the pair, linked from the header the way Ember's page did. */
const SEGMENT_SOURCE_PATH = '/workspaces/:workspaceId/integrations/segment_out';

type SegmentInPageProps = {
  workspaceId: string;
};

/**
 * Everything the page reads, so the body is layout alone.
 *
 * Both reads are keyed off the workspace the shell resolved rather than the URL:
 * this route renders under `/workspaces/last` before the sentinel is rewritten.
 */
function useSegmentInPageData(workspaceId: string) {
  const workspace = useCurrentWorkspace();
  const connection = useSegmentInConnection({ workspaceId: Number(workspaceId) });
  const credentials = useWorkspaceApiCredentials();

  return {
    workspaceName: workspace?.name ?? '',
    isEmailIdentified: workspace?.identifier_type === IdentifierType.Email,
    isMultiIdentifier: workspace?.identifier_type === IdentifierType.MultiIdentifier,
    enabledIdentifiers: allEnabledIdentifiers(workspace?.identifiers),
    connection: connection.data ?? null,
    credentials: credentials.data ?? [],
    isLoading: connection.isPending || credentials.isPending,
  };
}

/**
 * The `/integrations/segment_in` page: authorize with Segment, then create,
 * verify, disable or remove the Customer.io destination in the reader's Segment
 * workspace.
 *
 * Every action re-authorizes through the Segment popup, so the page has no saved
 * form — the connection itself is the state, and the panel reads it back after
 * each attempt.
 */
export default function SegmentInPage({ workspaceId }: SegmentInPageProps) {
  useSegmentInBreadcrumbs(workspaceId);

  const {
    workspaceName,
    isEmailIdentified,
    isMultiIdentifier,
    enabledIdentifiers,
    connection,
    credentials,
    isLoading,
  } = useSegmentInPageData(workspaceId);

  return (
    <Page>
      <DocumentTitle title={PAGE_TITLE} />
      <PageHeader aria-labelledby="segment-destination">
        <PageHeaderTitle id="segment-destination">
          <InlineStack alignBlock="center" gap="100">
            <Image src={segmentLogo} alt="" size="icon-v2-xl" objectFit="contain" data-test-integration-logo />
            {PAGE_TITLE}
          </InlineStack>
        </PageHeaderTitle>
        <PageHeaderDescription>
          Segment helps keep all your data in one place. With Customer.io as a Segment Destination, you can push data to
          Customer.io to feed into your automated messages. Learn more about{' '}
          <Link href={SEGMENT_DESTINATION_DOC_URL} variant="secondary" isExternal>
            Customer.io as a Segment Destination
          </Link>
          . You can also use{' '}
          <Link href={generatePath(SEGMENT_SOURCE_PATH, { workspaceId })} variant="secondary">
            Customer.io as a Segment Source
          </Link>
          .
        </PageHeaderDescription>
      </PageHeader>

      <PageBody>
        {/* Always shown: this destination is in maintenance mode and a newer one exists. */}
        <Banner
          title="This integration is in maintenance mode"
          variant="caution"
          w="full"
          mb="300"
          description={
            <>
              This Segment Destination has entered maintenance mode. Future updates are limited to security updates and
              bug fixes. A new version of this destination is available. See{' '}
              <Link href={SEGMENT_MIGRATE_DOC_URL} variant="secondary" isExternal>
                Customer.io (Actions)
              </Link>
              .
            </>
          }
        />

        {isLoading ? (
          <PageLoader label={LOADING_LABEL} />
        ) : (
          <Grid columns="8fr 4fr" gap="200">
            <Box>
              <SegmentInConnector
                workspaceId={workspaceId}
                workspaceName={workspaceName}
                connection={connection}
                credentials={credentials}
              />
            </Box>

            <Box>
              <SegmentHowItWorks
                isEmailIdentified={isEmailIdentified}
                isMultiIdentifier={isMultiIdentifier}
                enabledIdentifiers={enabledIdentifiers}
                workspaceSettingsHref={generatePath('/workspaces/:workspaceId/settings', { workspaceId })}
              />
            </Box>
          </Grid>
        )}
      </PageBody>
    </Page>
  );
}
